import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import BackButton from "@/components/BackButton";
import { useI18n } from "@/lib/i18n";
import { Compass, Leaf, ShoppingBag, HeartHandshake, Sparkles, Info } from "lucide-react";

/**
 * "La nostra missione" — approved static content, fully i18n driven.
 *
 * The Italian source of truth was recovered from git history (commit c0d38b5)
 * and is now stored as i18n keys (`missionpage_*`) with faithful translations
 * in EN/FR/DE/ES/PT. Changing language from the header updates the copy
 * instantly, without any manual page reload.
 *
 * Admin CMS overrides via PUT /api/pages/missione still take precedence when
 * `published:true`. The backend picks the correct language via ?lang=.
 */
export default function MissionPage() {
  const { t, lang } = useI18n();
  const [override, setOverride] = useState(null);

  // Fetch admin override in the current language. We accept it ONLY when
  // the backend actually served the requested language (`d.lang === lang`).
  // If admins have only published the Italian translation, the backend
  // would fall back to `it` for every other requested locale — accepting
  // that fallback would silently make the page monolingual Italian in
  // EN/FR/DE/ES/PT. We prefer the bundled multilingual copy in that case.
  useEffect(() => {
    let cancel = false;
    api.get(`/pages/missione?lang=${encodeURIComponent(lang)}`)
      .then((r) => {
        if (cancel) return;
        const d = r.data || {};
        const langMatches = !d.lang || d.lang === lang;
        if (d.published && langMatches && (d.title || d.body_md)) setOverride(d);
        else setOverride(null);
      })
      .catch(() => { if (!cancel) setOverride(null); });
    return () => { cancel = true; };
  }, [lang]);

  const sections = [
    { icon: Leaf, title: t("missionpage_s1_title"), body: t("missionpage_s1_body") },
    { icon: ShoppingBag, title: t("missionpage_s2_title"), body: t("missionpage_s2_body") },
    { icon: HeartHandshake, title: t("missionpage_s3_title"), body: t("missionpage_s3_body") },
  ];
  const rewards = [
    t("missionpage_rewards_1"),
    t("missionpage_rewards_2"),
    t("missionpage_rewards_3"),
    t("missionpage_rewards_4"),
    t("missionpage_rewards_5"),
    t("missionpage_rewards_6"),
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="mission-page">
      <AppHeader />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10" style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 24px)" }}>
        <BackButton hideMap />
        <div className="inline-flex items-center gap-2 chip bg-emerald-50 text-emerald-800 border border-emerald-200 mt-3 mb-3">
          <Compass size={12} /> {t("missionpage_kicker")}
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 leading-tight" data-testid="mission-title">
          {override?.title || t("missionpage_title")}
        </h1>

        {override?.body_md ? (
          <div
            data-testid="mission-body-override"
            className="mt-5 prose prose-slate max-w-none text-slate-700 whitespace-pre-line text-base leading-relaxed"
          >{override.body_md}</div>
        ) : (
          <>
            <p className="mt-4 text-slate-700 text-base sm:text-lg leading-relaxed" data-testid="mission-intro">
              {t("missionpage_intro")}
            </p>

            <div className="mt-6 grid gap-3 sm:gap-4">
              {sections.map((s, i) => (
                <div key={i} data-testid={`mission-section-${i}`} className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5">
                  <div className="flex items-center gap-2.5 mb-2">
                    <div className="w-9 h-9 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center"><s.icon size={16} /></div>
                    <div className="font-heading font-semibold text-slate-900 text-lg">{s.title}</div>
                  </div>
                  <p className="text-slate-700 text-sm sm:text-base leading-relaxed">{s.body}</p>
                </div>
              ))}
            </div>

            {/* Visione futura */}
            <div className="mt-6 bg-gradient-to-br from-emerald-50 to-white border border-emerald-200 rounded-2xl p-4 sm:p-5" data-testid="mission-future">
              <div className="flex items-center gap-2.5 mb-2">
                <div className="w-9 h-9 rounded-xl bg-emerald-600 text-white flex items-center justify-center"><Sparkles size={16} /></div>
                <div className="font-heading font-semibold text-slate-900 text-lg">{t("missionpage_future_title")}</div>
              </div>
              <p className="text-slate-700 text-sm sm:text-base leading-relaxed">{t("missionpage_future_p1")}</p>
              <p className="mt-2 text-slate-700 text-sm sm:text-base leading-relaxed">{t("missionpage_future_p2")}</p>
              <p className="mt-2 text-slate-700 text-sm sm:text-base leading-relaxed">{t("missionpage_future_p3")}</p>
            </div>

            {/* Reward examples */}
            <div className="mt-4 bg-white border border-slate-200 rounded-2xl p-4 sm:p-5" data-testid="mission-rewards">
              <div className="font-heading font-semibold text-slate-900 text-base mb-2">{t("missionpage_rewards_title")}</div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-sm text-slate-700">
                {rewards.map((r, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-2 shrink-0" />
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Mandatory disclaimer */}
            <div className="mt-4 bg-amber-50 border border-amber-300 rounded-2xl p-4 sm:p-5 flex gap-3" data-testid="mission-disclaimer">
              <Info size={18} className="text-amber-700 shrink-0 mt-0.5" />
              <p className="text-amber-900 text-sm leading-relaxed">{t("missionpage_disclaimer")}</p>
            </div>
          </>
        )}

        {override?.updated_at && (
          <div className="mt-4 text-[11px] text-slate-500" data-testid="mission-updated-at">
            {t("missionpage_updated_at")} {new Date(override.updated_at).toLocaleDateString(lang)}
          </div>
        )}
      </div>
    </div>
  );
}
